<template>
  <section
    class="my-8 md:mb-8 mx-auto bg-black pt-6 px-2 md:px-10 pb-0 shadow-black shadow-xl divide-y-2 divide-white font-helvetica text-[22px] text-[#999999] max-w-[1400px]"
  >
    <RallyLogoDownload />

    <!-- Language switcher -->
    <LanguageNav current="en" />

    <!-- Hero / intro -->
    <div class="pt-8 flex flex-col gap-4">
      <h1 class="text-white text-2xl md:text-4xl font-russo">
        Roadbook Creator &amp; Rally Road Book Maker
      </h1>
      <h2 class="font-semibold text-white">
        Design, Edit and Print Professional Rally Roadbooks
      </h2>
      <p class="text-[17px] md:text-[20px]">
        Rally Navigator is a complete roadbook creator for desktop &mdash; the
        roadbook maker and road book software used by organizers and competitors to build
        FIA-compliant roadbooks for cross-country, road rally and TSD (regularity rally) events.
        Draw your route with the
        <router-link to="/rally-maps" class="text-[#FF0000] hover:underline"
          >rally route planner</router-link
        >, capture distances and headings automatically, add tulip diagrams and
        navigation icons, then print a finished roadbook to PDF or export it as
        an OpenRally GPX file.
      </p>
      <RoadbookSamplePair />
    </div>

    <RoadbookCtas />

    <!-- Software available in your language -->
    <LanguageSupport
      heading="Available in 14 Languages"
      body="Rally Navigator’s menus and interface are fully translated into 14 languages — English, French, Spanish, German, Italian, Portuguese, Polish, Greek, Russian, Japanese, Arabic, Lithuanian, Bulgarian and Simplified Chinese. When you first install the app, a pop-up lets you choose your language — and you can change it any time via the Rally Navigator menu → “System and Menu Language”."
      img="language-selector"
      :width="502"
      :height="547"
      alt="The Rally Navigator Language Selector listing all 14 supported interface languages"
      map-langs="Rally Navigator also supports 38 languages for the map display and turn-by-turn directions — when you snap to roads, the navigation text in your roadbook is generated in the language you choose."
    />

    <!-- What is a roadbook -->
    <div class="py-8 mt-8 flex flex-col gap-4">
      <h2 class="text-white text-2xl md:text-3xl font-russo">
        What Is a Rally Roadbook?
      </h2>
      <p class="text-[17px] md:text-[20px]">
        A rally roadbook (or "road book") is the turn-by-turn navigation
        document a crew follows during a rally. Each waypoint shows the distance
        to the next instruction, a
        <router-link
          to="/rally-tulip-diagrams"
          class="text-[#FF0000] hover:underline"
          >tulip diagram</router-link
        >
        of the junction, a compass
        heading, and navigation icons or notes that warn of hazards and
        landmarks. A clear, accurate roadbook is what keeps competitors on
        course &mdash; and producing one by hand is slow and error-prone.
      </p>
      <p class="text-[17px] md:text-[20px]">
        Rally Navigator is the roadbook app that automates the hard parts:
        measuring segment and total distances, generating tulips, and laying out
        a print-ready page that meets FIA roadbook specifications.
      </p>
      <picture class="contents">
        <source srcset="/images/create-road-stage/RR-Screenshot-1.webp" type="image/webp" />
        <img
          decoding="async"
          loading="lazy"
          width="1919"
          height="1029"
          src="/images/create-road-stage/RR-Screenshot-1.png"
          class="flex m-auto"
          alt="Rally roadbook creator software showing a route, tulip diagrams and waypoints on the map"
        />
      </picture>
    </div>

    <!-- Roadbook creator vs. making one by hand -->
    <div class="py-8 mt-8 flex flex-col gap-4">
      <h2 class="text-white text-2xl md:text-3xl font-russo">
        Why Use a Roadbook Creator Instead of Making One by Hand?
      </h2>
      <p class="text-[17px] md:text-[20px]">
        The traditional way to make a roadbook is to drive the route with a trip
        meter, note every junction, sketch the tulips on paper and then type it
        all up page by page. A single change to the route means re-measuring and
        re-numbering everything after it. A roadbook creator does that work for
        you:
      </p>
      <ul
        class="list-disc pl-8 text-left py-2 flex flex-col gap-3 font-arial text-[16px] md:text-[18px] font-bold"
      >
        <li>
          Distances, partial distances and CAP headings are calculated from the
          map, and recalculate automatically when you move, add or delete a
          waypoint.
        </li>
        <li>
          Tulips are generated from the road geometry, so you only fine-tune
          them instead of drawing each one from scratch.
        </li>
        <li>
          The roadbook maker lays out FIA-style pages for you &mdash; print to
          PDF in A4, A5, Letter, Legal or roll format without touching a word
          processor.
        </li>
        <li>
          The same file produces the GPX for tracking and timing, so the paper
          roadbook and the GPS data always match.
        </li>
      </ul>
      <p class="text-[17px] md:text-[20px]">
        You still recce the route &mdash; but you spend that time checking the
        navigation, not re-measuring it.
      </p>
    </div>

    <!-- Roadbook software for every discipline -->
    <div class="py-8 mt-8 flex flex-col gap-4">
      <h2 class="text-white text-2xl md:text-3xl font-russo">
        One Roadbook Editor for Every Discipline
      </h2>
      <p class="text-[17px] md:text-[20px]">
        Whether you run a desert rally-raid, a classic road rally or a Time
        Speed Distance (regularity) event, Rally Navigator gives you a single roadbook
        designer with the right tools for the job:
      </p>
      <ul
        class="list-disc pl-8 text-left py-2 flex flex-col gap-3 font-arial text-[16px] md:text-[18px] font-bold"
      >
        <li>
          <router-link
            to="/how-to-create-a-cross-country"
            class="text-[#FF0000] hover:underline"
            >Cross-Country &amp; Rally-Raid Roadbooks</router-link
          >
          &mdash; waypoint-based navigation with validation radius, CAP
          headings and Dakar-style tulips.
        </li>
        <li>
          <router-link
            to="/how-to-create-a-road-rally"
            class="text-[#FF0000] hover:underline"
            >Road &amp; Stage Rally Roadbooks</router-link
          >
          &mdash; snap-to-road routing and FIA-legal page layouts.
        </li>
        <li>
          <router-link
            to="/time-speed-distance"
            class="text-[#FF0000] hover:underline"
            >Time Speed Distance (TSD) &amp; Regularity Rally Roadbooks</router-link
          >
          &mdash; precise timing, average-speed and checkpoint calculations.
        </li>
      </ul>
    </div>

    <!-- How it works -->
    <div class="py-8 mt-8 flex flex-col gap-4">
      <h2 class="text-white text-2xl md:text-3xl font-russo">
        How to Create a Roadbook in Rally Navigator
      </h2>
      <picture class="contents">
        <source srcset="/images/download/toolbar3-1.webp" type="image/webp" />
        <img
          decoding="async"
          loading="lazy"
          width="1920"
          height="1080"
          src="/images/download/toolbar3-1.png"
          class="flex m-auto"
          alt="Rally Navigator desktop app: route on the satellite map, roadbook rows with tulips and CAP headings, and the Note and Signs icon palettes"
        />
      </picture>
      <ul
        class="list-disc pl-8 text-left py-2 flex flex-col gap-3 font-arial text-[16px] md:text-[18px] font-bold"
      >
        <li>
          Draw your route on the MapBox map, or import an existing GPX or KML
          track to get started.
        </li>
        <li>
          Use snap-to-roads to trace roads automatically, then double-click to
          create a waypoint at every junction &mdash; distances, headings and a
          tulip are captured for you.
        </li>
        <li>
          Drag and drop FIA / Dakar navigation icons, shape the tulip to match
          the real intersection, and add text notes and background images.
        </li>
        <li>
          Review elevation, speed limits and Street View snapshots to make the
          roadbook navigation crystal clear.
        </li>
      </ul>
    </div>

    <!-- Print & export -->
    <div class="py-8 mt-8 flex flex-col gap-4">
      <h2 class="text-white text-2xl md:text-3xl font-russo">
        Print Roadbooks to PDF &amp; Export OpenRally GPX
      </h2>
      <p class="text-[17px] md:text-[20px]">
        When your route is ready, print a polished roadbook PDF in A4, A5,
        Letter, Legal or continuous moto-roll formats &mdash; ready for the
        printer or the roadbook holder. Export an OpenRally GPX with embedded
        scoring and control data for your timing system, and share your digital
        roadbook to the mobile reader.
      </p>
      <picture class="contents">
        <source srcset="/images/OpenRallyGPX-Screen-Shot.webp" type="image/webp" />
        <img
          decoding="async"
          loading="lazy"
          width="1200"
          height="700"
          src="/images/OpenRallyGPX-Screen-Shot.png"
          class="flex m-auto"
          alt="Exporting a rally roadbook as an OpenRally GPX file with scoring and control data"
        />
      </picture>
    </div>

    <!-- Digital roadbook / mobile -->
    <div class="py-8 mt-8 flex flex-col gap-4">
      <h2 class="text-white text-2xl md:text-3xl font-russo">
        Your Digital Roadbook, On Any Device
      </h2>
      <p class="text-[17px] md:text-[20px]">
        Beyond paper, every roadbook you build can become a live digital
        roadbook. Share it to the
        <router-link
          to="/mobile-roadbook-reader"
          class="text-[#FF0000] hover:underline"
          >Rally Roadbook Reader</router-link
        >
        app for iOS and Android to navigate with an adjustable odometer and CAP
        heading during the event.
      </p>
      <router-link to="/download" class="m-auto">
        <h2
          class="text-[#FF0000] font-russo text-[22px] md:text-[28px] hover:underline text-center"
        >
          Download Rally Navigator and Build Your First Roadbook Free
        </h2>
      </router-link>
    </div>
  </section>

  <section class="my-8 mx-auto max-w-[1400px]">
    <FeaturesBanner />
    <PartnersSmall />
    <RallyLogoDownload link-text="Download Rally Navigator" />
  </section>
</template>

<script>
import FeaturesBanner from './FeaturesBanner.vue';
import PartnersSmall from './PartnersSmall.vue';
import RallyLogoDownload from './RallyLogoDownload.vue';
import LanguageSupport from './LanguageSupport.vue';
import RoadbookCtas from './RoadbookCtas.vue';
import RoadbookSamplePair from './RoadbookSamplePair.vue';
import LanguageNav from './LanguageNav.vue';

export default {
  name: 'RallyRoadbook',
  components: {
    FeaturesBanner,
    PartnersSmall,
    RallyLogoDownload,
    LanguageSupport,
    RoadbookCtas,
    RoadbookSamplePair,
    LanguageNav,
  },
};
</script>

<style scoped></style>
